import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Bell, ShieldCheck, Zap, TrendingUp, ExternalLink, Send, Trophy, Ticket } from "lucide-react";
import PublicNav from "../components/PublicNav";
import MarketTicker from "../components/MarketTicker";
import WinRateDonut from "../components/WinRateDonut";
import { LotteryCountdown } from "../components/LotteryCountdown";
import { CupPodium } from "../components/CupPodium";
import { monthLabel } from "./Championship";
import { StrategyPnlModal } from "../components/StrategyPnlModal";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import api from "../lib/api";
import { logError } from "../lib/logger";

const HERO = "https://images.unsplash.com/photo-1611974789855-9c2a0a7236a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NDh8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGRhcmslMjBmaW5hbmNpYWwlMjBzdG9jayUyMG1hcmtldCUyMGdyYXBofGVufDB8fHxibGFja3wxNzgzMzc3Njk4fDA&ixlib=rb-4.1.0&q=85";
const ROI_IMG = "https://customer-assets.emergentagent.com/job_market-pulse-pro-48/artifacts/dnxrb94c_ROI%20128.png";
const BROKER_URL = "https://puvip.co/ai6vAn";
const CHAMPIONSHIP_URL = "https://www.worldcupchampionships.com/world-cup-trading-championship-standings";
const TELEGRAM = "https://t.me/raeinexchangesupport";

const assets = [
  { s: "XAUUSD", n: "Oro / Dollaro", c: "text-amber-400" },
  { s: "BTCUSD", n: "Bitcoin", c: "text-orange-400" },
  { s: "ETHUSD", n: "Ethereum", c: "text-blue-400" },
  { s: "EURUSD", n: "Euro / Dollaro", c: "text-emerald-400" },
  { s: "USDJPY", n: "Dollaro / Yen", c: "text-emerald-400" },
  { s: "EURGBP", n: "Euro / Sterlina", c: "text-emerald-400" },
  { s: "GBPUSD", n: "Sterlina / Dollaro", c: "text-emerald-400" },
];

const features = [
  { icon: Bell, t: "Notifiche Push in Tempo Reale", d: "Ricevi ogni segnale direttamente sul tuo browser, anche quando l'app è chiusa." },
  { icon: TrendingUp, t: "Segnali Multi-Asset", d: "XAUUSD, Bitcoin, criptovalute e coppie forex, tutto in un'unica dashboard." },
  { icon: Zap, t: "Entry, SL e TP Precisi", d: "Ogni segnale include prezzo di ingresso, stop loss e take profit calcolati in base alla variazione percentuale del pair e il timeframe del segnale." },
  { icon: ShieldCheck, t: "Strategia Verificata", d: "Segnali basati su una strategia proprietaria testata sui mercati da anni." },
];

export default function Landing() {
  const [stats, setStats] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [lottery, setLottery] = useState(null);
  const [cup, setCup] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [pnlStrategy, setPnlStrategy] = useState(null);

  useEffect(() => {
    api.get("/public/stats").then((r) => setStats(r.data)).catch((e) => logError("public stats", e));
    api.get("/public/pnl-by-asset").then((r) => setPnl(r.data)).catch((e) => logError("pnl by asset", e));
    api.get("/lottery").then((r) => setLottery(r.data)).catch((e) => logError("lottery", e));
    api.get("/championship/winners").then((r) => setCup(r.data)).catch((e) => logError("cup winners", e));
  }, []);

  const assetPnl = (sym) => (pnl?.enabled ? pnl.assets?.[sym] : null);
  const selData = selectedAsset ? assetPnl(selectedAsset.s) : null;

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <PublicNav />
      <StrategyPnlModal strategy={pnlStrategy} onClose={() => setPnlStrategy(null)} />
      <MarketTicker />

      <section className="relative overflow-hidden">
        <img src={HERO} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-black/60 grid-bg" />
        <div className="relative max-w-7xl mx-auto px-6 py-28 lg:py-40">
          <div className="max-w-3xl animate-in-up">
            <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-blue-500/15 text-blue-400 border border-blue-500/20 mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-blue-400 pulse-dot" /> SEGNALI LIVE 24/7
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Il tuo consulente finanziario personale AI:<br />
              <span className="text-blue-500">Raeinexchange</span>
            </h1>
            <p className="mt-6 text-lg text-zinc-300 max-w-xl leading-relaxed">
              Ricevi segnali su XAUUSD, Bitcoin, criptovalute e coppie di valute direttamente sul tuo dispositivo.
              Sarai notificato in tempo reale sulle operazioni e potrai sfruttare NEWS AI per la lettura dei dati NFP.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Link data-testid="hero-cta-btn" to="/auth"
                className="px-6 py-3 rounded-md bg-white text-black font-medium hover:bg-zinc-200 transition-colors">
                Inizia gratis
              </Link>
              <Link data-testid="hero-pricing-btn" to="/auth"
                className="px-6 py-3 rounded-md border border-white/20 text-white font-medium hover:bg-white/5 transition-colors">
                Scopri Premium
              </Link>
            </div>
            <div className="mt-12" data-testid="landing-assets">
              <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
                Asset su cui opera la piattaforma
              </div>
              <div className="flex flex-wrap gap-3">
                {assets.map((a) => {
                  const p = assetPnl(a.s);
                  const clickable = !!p;
                  return (
                    <button
                      key={a.s}
                      type="button"
                      data-testid={`landing-asset-${a.s}`}
                      disabled={!clickable}
                      onClick={clickable ? () => setSelectedAsset(a) : undefined}
                      className={`text-left px-3 py-2 rounded-md bg-[#141414]/80 border border-white/10 backdrop-blur transition-colors ${
                        clickable ? "cursor-pointer hover:border-emerald-500/50 hover:bg-[#1a1a1a]" : "cursor-default opacity-90"
                      }`}
                    >
                      <div className={`font-mono text-sm font-semibold ${a.c}`}>{a.s}</div>
                      <div className="text-[11px] text-zinc-500">{a.n}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {stats && (
              <div className="mt-10" data-testid="landing-winrate">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-400 mb-3">
                  Win rate reale dei nostri segnali
                </div>
                <div className="flex flex-wrap gap-4">
                  <WinRateDonut testid="landing-donut-overall" label="Complessivo" size={108}
                    value={stats.win_rate} tp={stats.tp_hit} sl={stats.sl_hit} closed={stats.closed} color="#10B981"
                    onClick={() => setPnlStrategy({ key: "ALL", label: "Complessivo", color: "#10B981" })} />
                  {(stats.by_strategy || []).map((s) => {
                    const meta = {
                      "LIQUIDITY POOL": { label: "Liquidity Pool", color: "#3B82F6", testid: "liquidity-pool" },
                      "STOCH TREND": { label: "Stoch Trend", color: "#F59E0B", testid: "stoch-trend" },
                      "FAIR LIQUIDITY GAP": { label: "Fair Liquidity", color: "#14B8A6", testid: "fair-liquidity-gap" },
                    }[s.label] || { label: s.label, color: "#71717A", testid: "strategy" };
                    return (
                      <WinRateDonut key={s.label}
                        testid={`landing-donut-${meta.testid}`}
                        label={meta.label} size={108}
                        value={s.win_rate} tp={s.tp_hit} sl={s.sl_hit} closed={s.closed}
                        color={meta.color}
                        onClick={() => setPnlStrategy({ key: s.label, label: meta.label, color: meta.color })} />
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pt-16" data-testid="landing-lottery-section">
        <Link to="/lottery" className="block group">
          <div className="rounded-2xl border border-amber-500/25 bg-gradient-to-br from-amber-500/[0.08] to-transparent p-6 sm:p-8 hover:border-amber-500/45 transition-colors">
            <div className="flex flex-col lg:flex-row lg:items-center gap-6 justify-between">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 mb-4">
                  <Ticket size={13} /> LOTTERIA VIP
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
                  Estrazione <span className="text-amber-400">premi</span> per gli abbonati
                </h2>
                <p className="mt-2 text-zinc-300 max-w-xl">
                  Ogni iscritto ha un numero fortunato univoco. Iscriviti e attiva il VIP per partecipare al prossimo sorteggio.
                  {lottery?.prize ? <> Premio in palio: <span className="text-white font-medium">{lottery.prize}</span>.</> : null}
                </p>
              </div>
              <div className="flex flex-col items-start lg:items-end gap-3">
                <LotteryCountdown target={lottery?.next_draw_at} compact />
                <span className="text-sm font-medium text-amber-400 group-hover:text-amber-300 transition-colors">
                  Vai alla lotteria →
                </span>
              </div>
            </div>
          </div>
        </Link>
      </section>

      {cup?.winners?.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 pt-16" data-testid="landing-cup-winners">
          <div className="rounded-2xl border border-amber-500/25 bg-[#111] p-6 sm:p-10">
            <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
              <div>
                <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 border border-amber-500/20 mb-4">
                  <Trophy size={13} /> TRADING CUP · {monthLabel(cup.month).toUpperCase()}
                </span>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight leading-tight">
                  I vincitori dell'ultima <span className="text-amber-400">Trading Cup</span>
                </h2>
              </div>
              <Link to="/championship" data-testid="landing-cup-link"
                className="text-sm font-medium text-amber-400 hover:text-amber-300 transition-colors">
                Vedi classifica completa →
              </Link>
            </div>
            <CupPodium entries={cup.winners} />
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 pt-20" data-testid="landing-trading-section">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <span className="inline-flex items-center gap-2 text-xs font-mono px-3 py-1 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 mb-6">
              TRADING
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight leading-tight mb-5">
              Fai trading con i miei stessi <span className="text-emerald-400">privilegi</span>
            </h2>
            <p className="text-lg text-zinc-300 leading-relaxed">
              Se vuoi fare trading con i miei stessi privilegi, <span className="text-white font-medium">0 commissioni</span> e
              <span className="text-white font-medium"> spread contenuto</span>, registrati pure dal link.
            </p>
            <p className="mt-5 text-lg text-zinc-300 leading-relaxed">
              Se mi seguirai passo passo, potrai anche tu diventare (come me) un campione di trading con un
              <span className="text-amber-400 font-semibold"> ROI mensile di oltre il 125%</span>.
            </p>
            <div className="mt-7 flex flex-wrap gap-4">
              <a href={BROKER_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-broker-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-emerald-500 text-black font-semibold hover:bg-emerald-400 transition-colors">
                Registrati ora <ExternalLink size={16} />
              </a>
              <a href={TELEGRAM} target="_blank" rel="noopener noreferrer" data-testid="landing-telegram-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-white/20 text-white font-medium hover:bg-white/5 transition-colors">
                <Send size={16} /> Scrivimi su Telegram
              </a>
            </div>
          </div>
          <a href={CHAMPIONSHIP_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-roi-image"
            className="block group rounded-lg overflow-hidden border border-white/10 hover:border-amber-500/50 transition-colors">
            <img src={ROI_IMG} alt="World Cup Trading Championship - Paolo Bonfanti 128.4%" className="w-full h-auto block" />
            <div className="flex items-center justify-center gap-2 py-3 bg-[#141414] text-sm text-amber-400 group-hover:text-amber-300 transition-colors">
              <Trophy size={16} /> Vedi la classifica ufficiale <ExternalLink size={14} />
            </div>
          </a>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-24">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f) => (
            <div key={f.t} className="bg-[#141414] border border-white/10 rounded-md p-6 hover:border-white/30 transition-colors">
              <f.icon size={24} className="text-blue-500 mb-4" strokeWidth={2} />
              <h3 className="text-lg font-semibold tracking-tight mb-2">{f.t}</h3>
              <p className="text-sm text-zinc-400 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-white/10 py-8 text-center text-sm text-zinc-600">
        RaeinExchange © 2026 · I segnali non costituiscono consulenza finanziaria.
      </footer>

      <Dialog open={!!selectedAsset} onOpenChange={(o) => !o && setSelectedAsset(null)}>
        <DialogContent className="bg-[#141414] border-white/10 text-white sm:max-w-md" data-testid="asset-pnl-modal">
          {selectedAsset && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <span className={`font-mono ${selectedAsset.c}`}>{selectedAsset.s}</span>
                  <span className="text-sm font-normal text-zinc-500">{selectedAsset.n}</span>
                </DialogTitle>
              </DialogHeader>
              <div className="mt-2">
                <div className="text-xs font-mono uppercase tracking-wider text-zinc-500 mb-2">
                  PnL totale generato (conto reale MT5)
                </div>
                <div
                  data-testid="asset-pnl-value"
                  className={`text-4xl font-bold ${
                    (selData?.pnl || 0) >= 0 ? "text-emerald-400" : "text-red-400"
                  }`}
                >
                  {(selData?.pnl || 0) >= 0 ? "+" : ""}
                  {(selData?.pnl || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €
                </div>
                <div className="mt-3 text-sm text-zinc-400">
                  Basato su {selData?.trades || 0} operazioni chiuse negli ultimi 12 mesi.
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
